import { useState } from "react";
import { Building2, User } from "lucide-react";
import { PageFull, Toolbar } from "@praesto/ui-kit";
import { useCore } from "../lib/core";
import { SideNav, type SideNavItem } from "../components/SideNav";
import { PersonalSettings } from "./PersonalSettings";
import { TenantSettings } from "./TenantSettings";
import { useHelpAnchor } from "../help/HelpHost";

/**
 * Settings: personal preferences (everyone) + tenant settings (owner only).
 * Owner gate uses `can("manage","hotel")` (owner-only permission).
 */
export function SettingsHome() {
  const core = useCore();
  const t = core.i18n.t;
  const isOwner = core.rbac.can("manage", "hotel");
  const [tab, setTab] = useState<"personal" | "tenant">("personal");
  // The open panel is local state, so the `?` cannot tell the two apart on its own.
  useHelpAnchor(tab === "tenant" ? "settings.tenant" : "settings.personal");

  const items: SideNavItem[] = [
    {
      key: "personal",
      label: t("settings.tab.personal"),
      icon: User,
      onClick: () => setTab("personal"),
      active: tab === "personal",
    },
    ...(isOwner
      ? [
          {
            key: "tenant",
            label: t("settings.tab.tenant"),
            icon: Building2,
            onClick: () => setTab("tenant"),
            active: tab === "tenant",
          } as SideNavItem,
        ]
      : []),
  ];

  return (
    <section className="flex flex-1 flex-col md:flex-row">
      <SideNav title={t("settings.title")} items={items} />
      <div className="flex-1 min-w-0">
        <PageFull>
          {/* On desktop the rail header is the section title; show this only on
              mobile (rail hidden) so the title isn't duplicated. */}
          <div className="md:hidden">
            <Toolbar title={t("settings.title")} />
          </div>
          <div className="mt-4">
            {tab === "tenant" && isOwner ? <TenantSettings /> : <PersonalSettings />}
          </div>
        </PageFull>
      </div>
    </section>
  );
}
